(function () {
  const MOBILE_QUERY = '(max-width: 989px)';

  function defineScrollableCarousel(tagName, config) {
    if (customElements.get(tagName)) return;

    const { trackSelector, cardSelector, fadeSelector, linkSelector } = config;

    class ScrollableCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector(trackSelector);
        this.fadeEnd = fadeSelector ? this.querySelector(fadeSelector) : null;

        if (!this.track) return;

        this.cardSelector = cardSelector;
        this.linkSelector = linkSelector;
        this.mediaQuery = window.matchMedia(MOBILE_QUERY);
        this.cards = [];
        this.pointerId = null;
        this.dragStartX = 0;
        this.dragScrollLeft = 0;
        this.dragMoved = false;
        this.suppressClick = false;

        this.onScroll = this.onScroll.bind(this);
        this.refresh = this.refresh.bind(this);
        this.onMediaChange = this.onMediaChange.bind(this);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.mediaQuery.addEventListener('change', this.onMediaChange);

        this.initDrag();
        this.initKeyboard();
        this.onMediaChange();
      }

      disconnectedCallback() {
        this.mediaQuery?.removeEventListener('change', this.onMediaChange);
        this.track?.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
      }

      onMediaChange() {
        if (!this.mediaQuery.matches) {
          this.classList.remove('is-scrollable');
          this.fadeEnd?.classList.remove('is-visible');
          this.resizeObserver?.disconnect();
          this.resizeObserver = null;
          return;
        }

        if (!this.resizeObserver) {
          this.resizeObserver = new ResizeObserver(this.refresh);
          this.resizeObserver.observe(this.track);
          if (this.track.firstElementChild) {
            this.resizeObserver.observe(this.track.firstElementChild);
          }
        }

        this.refresh();
      }

      refresh() {
        if (!this.mediaQuery.matches) return;

        this.cards = Array.from(this.track.querySelectorAll(this.cardSelector));
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
        this.classList.toggle('is-scrollable', scrollable);
        this.updateUI();
      }

      getCardScrollLeft(card) {
        return card.offsetLeft - this.track.offsetLeft;
      }

      getActiveIndex() {
        if (!this.cards.length) return 0;

        const scrollLeft = this.track.scrollLeft;
        let activeIndex = 0;
        let smallestDistance = Infinity;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(this.getCardScrollLeft(card) - scrollLeft);
          if (distance < smallestDistance) {
            smallestDistance = distance;
            activeIndex = index;
          }
        });

        return activeIndex;
      }

      scrollToCard(index, behavior) {
        const card = this.cards[index];
        if (!card) return;

        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({
          left: this.getCardScrollLeft(card),
          behavior: behavior ?? (prefersReduced ? 'auto' : 'smooth'),
        });
      }

      snapToNearestCard() {
        if (!this.mediaQuery.matches || !this.cards.length) return;
        this.scrollToCard(this.getActiveIndex(), 'smooth');
      }

      onScroll() {
        if (!this.mediaQuery.matches || this.pointerId !== null) return;
        this.updateUI();
      }

      updateUI() {
        if (!this.mediaQuery.matches) return;

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const atEnd = this.track.scrollLeft >= maxScroll - 2;
        this.fadeEnd?.classList.toggle('is-visible', !atEnd && this.classList.contains('is-scrollable'));
      }

      initKeyboard() {
        this.track.addEventListener('keydown', (event) => {
          if (!this.mediaQuery.matches || !this.classList.contains('is-scrollable')) return;

          const activeIndex = this.getActiveIndex();
          if (event.key === 'ArrowRight' && activeIndex < this.cards.length - 1) {
            event.preventDefault();
            this.scrollToCard(activeIndex + 1);
          } else if (event.key === 'ArrowLeft' && activeIndex > 0) {
            event.preventDefault();
            this.scrollToCard(activeIndex - 1);
          }
        });
      }

      initDrag() {
        this.track.addEventListener('pointerdown', (event) => {
          if (!this.mediaQuery.matches || !this.classList.contains('is-scrollable')) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;

          this.pointerId = event.pointerId;
          this.dragMoved = false;
          this.dragStartX = event.clientX;
          this.dragScrollLeft = this.track.scrollLeft;
          this.track.classList.add('is-dragging');

          if (this.track.setPointerCapture) {
            this.track.setPointerCapture(event.pointerId);
          }
        });

        this.track.addEventListener('pointermove', (event) => {
          if (event.pointerId !== this.pointerId) return;

          const delta = event.clientX - this.dragStartX;
          if (Math.abs(delta) > 4) {
            this.dragMoved = true;
          }

          if (!this.dragMoved) return;

          event.preventDefault();
          this.track.scrollLeft = this.dragScrollLeft - delta;
          this.updateUI();
        });

        const endDrag = (event) => {
          if (event.pointerId !== this.pointerId) return;

          if (this.track.releasePointerCapture) {
            try {
              this.track.releasePointerCapture(event.pointerId);
            } catch (error) {
              /* pointer may already be released */
            }
          }

          const didDrag = this.dragMoved;
          this.pointerId = null;
          this.track.classList.remove('is-dragging');

          if (didDrag) {
            this.suppressClick = true;
            window.setTimeout(() => {
              this.suppressClick = false;
            }, 0);
            this.snapToNearestCard();
          }

          this.updateUI();
        };

        this.track.addEventListener('pointerup', endDrag);
        this.track.addEventListener('pointercancel', endDrag);

        this.track.addEventListener(
          'click',
          (event) => {
            if (!this.suppressClick) return;
            event.preventDefault();
            event.stopImmediatePropagation();
          },
          true
        );
      }
    }

    customElements.define(tagName, ScrollableCarousel);
  }

  defineScrollableCarousel('home-fifth-carousel', {
    trackSelector: '.home-fifth__grid',
    cardSelector: '.home-fifth__card',
    fadeSelector: '.home-fifth__fade--end',
    linkSelector: '.home-fifth__card-inner',
  });

  defineScrollableCarousel('home-sixth-carousel', {
    trackSelector: '.home-sixth__grid',
    cardSelector: '.home-sixth__card',
    fadeSelector: '.home-sixth__fade--end',
    linkSelector: '.home-sixth__video-poster',
  });
})();
